import React, { useState } from 'react';
import { 
  Container, Typography, Box, Button, Paper, 
  Stepper, Step, StepLabel, TextField, FormControl,
  FormLabel, RadioGroup, Radio, FormControlLabel,
  Alert, CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import { ProjectResponse, RequirementType } from '../types';
import projectService from '../services/projectService';
import requirementService from '../services/requirementService';
import { extractErrorMessage } from '../utils/errorHandler';

const steps = ['Select Requirement Type', 'Upload File', 'Project Details'];

const RequirementUpload: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [requirementType, setRequirementType] = useState<RequirementType>(RequirementType.DOCUMENT);
  const [file, setFile] = useState<File | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const projectResponse: ProjectResponse = await projectService.createProject({
        name: projectName,
        description: projectDescription,
        requirementType,
      });

      if (!projectResponse || projectResponse.status !== 'success') {
        throw new Error('Failed to create project. Please try again.');
      }
  
      const projectId = projectResponse.data.id;

      const uploadResponse = await requirementService.uploadRequirement(projectId, requirementType, file);

      if (!uploadResponse || uploadResponse.status !== 'success') {
        throw new Error('Failed to upload requirement. Please try again.');
      } else {
        alert('Requirement uploaded successfully!');
        navigate('/');
      }
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getAcceptedFileTypes = () => {
    switch (requirementType) {
      case RequirementType.AUDIO:
        return '.mp3,.wav,.m4a';
      case RequirementType.VIDEO:
        return '.mp4,.avi,.mov';
      case RequirementType.DOCUMENT:
      default:
        return '.pdf,.doc,.docx,.txt';
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">Requirement Type</FormLabel>
            <RadioGroup
              value={requirementType}
              onChange={(e) => setRequirementType(e.target.value as RequirementType)}
            >
              <FormControlLabel 
                value={RequirementType.DOCUMENT} 
                control={<Radio />} 
                label="Document (Word, Text)" 
              />
              <FormControlLabel 
                value={RequirementType.AUDIO} 
                control={<Radio />} 
                label="Audio Recording (MP3, WAV)" 
              />
              <FormControlLabel 
                value={RequirementType.VIDEO} 
                control={<Radio />} 
                label="Video Recording (MP4, AVI)" 
              />
            </RadioGroup>
          </FormControl>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <input
              accept={getAcceptedFileTypes()}
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="raised-button-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Select {requirementType.toLowerCase()} file
              </Button>
            </label>
            {file && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                Selected file: {file.name}
              </Typography>
            )}
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Project Name"
              variant="outlined"
              fullWidth
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              sx={{ mb: 3 }}
              required
            />
            <TextField
              label="Project Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              required
            />
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Upload Requirement
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {getStepContent(activeStep)}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0 || isLoading}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!file || !projectName || !projectDescription || isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && !requirementType) ||
                  (activeStep === 1 && !file) ||
                  isLoading
                }
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RequirementUpload;